import React from 'react';
import { Mic, Square, CheckCircle, Clock, Sparkles } from 'lucide-react';

const RecordTab = ({
    isRecording,
    recordingTime,
    audioCategory,
    setAudioCategory,
    isProcessing,
    showSuccessMessage,
    startRecording,
    stopRecording,
    formatTime,
    compact = false
}) => {
    const shellClass = compact
        ? 'bg-white/90 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-gray-200/50 shadow-md max-w-lg mx-auto'
        : 'bg-white/90 backdrop-blur-xl rounded-3xl p-12 border border-gray-200/50 shadow-xl max-w-2xl mx-auto';

    const iconSize = compact ? 'w-24 h-24' : 'w-40 h-40';
    const micSize = compact ? 'w-11 h-11' : 'w-20 h-20';
    const titleClass = compact ? 'text-lg font-semibold text-gray-900' : 'text-3xl font-bold text-gray-900';
    const subClass = compact ? 'text-sm text-gray-600' : 'text-gray-600 text-lg';
    const timerClass = compact ? 'text-xl font-bold text-gray-900' : 'text-2xl font-bold text-gray-900';
    const btnClass = compact
        ? 'group inline-flex items-center px-6 py-3 text-base rounded-xl font-semibold'
        : 'group inline-flex items-center px-10 py-5 text-xl rounded-2xl font-semibold';

    return (
        <div className={compact ? 'max-w-lg mx-auto' : 'max-w-2xl mx-auto'}>
            {showSuccessMessage && (
                <div className={`${compact ? 'mb-4 p-3' : 'mb-6 p-4'} bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl shadow-sm`}>
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 mr-3 text-green-600 shrink-0" />
                        <span className="font-medium text-green-800 text-sm">Recording saved successfully!</span>
                    </div>
                </div>
            )}

            {isProcessing && (
                <div className={`${compact ? 'mb-4 p-3' : 'mb-6 p-4'} bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl shadow-sm`}>
                    <div className="flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3 shrink-0" />
                        <span className="font-medium text-blue-800 text-sm">Processing your recording...</span>
                    </div>
                </div>
            )}

            <div className={shellClass}>
                <div className={`text-center ${compact ? 'space-y-5' : 'space-y-8'}`}>
                    <div className="relative">
                        <div
                            className={`${iconSize} ${
                                isRecording
                                    ? 'bg-gradient-to-br from-red-500 via-pink-500 to-red-600 animate-pulse shadow-xl shadow-red-500/30'
                                    : 'bg-gradient-to-br from-blue-500 via-indigo-500 to-blue-600 shadow-xl shadow-blue-500/30'
                            } rounded-full flex items-center justify-center mx-auto transition-all duration-500`}
                        >
                            <Mic className={`${micSize} text-white ${isRecording ? 'animate-bounce' : ''}`} />
                        </div>
                        {isRecording && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                                <div className="w-2 h-2 bg-white rounded-full" />
                            </div>
                        )}
                    </div>

                    <div className={compact ? 'space-y-1.5' : 'space-y-3'}>
                        {isRecording ? (
                            <>
                                <div className={`flex items-center justify-center space-x-2 ${timerClass}`}>
                                    <Clock className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} text-red-500`} />
                                    <span>{formatTime(recordingTime)}</span>
                                </div>
                                <p className={subClass}>Recording in progress...</p>
                            </>
                        ) : (
                            <>
                                <h3 className={titleClass}>Ready to record</h3>
                                <p className={subClass}>Transform your voice into organized knowledge</p>
                            </>
                        )}
                    </div>

                    <div className="flex items-center justify-center">
                        <div className="relative">
                            <select
                                value={audioCategory}
                                onChange={(e) => setAudioCategory(e.target.value)}
                                disabled={isRecording}
                                className={`appearance-none bg-white/80 backdrop-blur border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 transition-all duration-200 text-gray-700 font-medium shadow-sm ${
                                    compact ? 'px-4 py-2 text-sm' : 'px-6 py-3 rounded-2xl'
                                }`}
                            >
                                <option value="meeting">📅 Meeting</option>
                                <option value="interview">🎤 Interview</option>
                                <option value="lecture">🎓 Lecture</option>
                                <option value="personal">💭 Personal Note</option>
                            </select>
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className={compact ? 'pt-1' : 'pt-4'}>
                        {!isRecording ? (
                            <button
                                onClick={startRecording}
                                className={`${btnClass} bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]`}
                            >
                                <Mic className={`${compact ? 'w-5 h-5 mr-2' : 'w-7 h-7 mr-3'} group-hover:scale-110 transition-transform`} />
                                Start recording
                            </button>
                        ) : (
                            <button
                                onClick={stopRecording}
                                className={`${btnClass} bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]`}
                            >
                                <Square className={`${compact ? 'w-5 h-5 mr-2' : 'w-7 h-7 mr-3'} group-hover:scale-110 transition-transform`} />
                                Stop & save
                            </button>
                        )}
                    </div>

                    {isProcessing && (
                        <div className="flex items-center justify-center space-x-2 text-blue-600 font-medium text-sm">
                            <Sparkles className="w-4 h-4 animate-pulse" />
                            <span>AI is analyzing your recording...</span>
                        </div>
                    )}
                </div>
            </div>

            {!compact && !isRecording && !isProcessing && (
                <div className="mt-8 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200/50">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                        <Sparkles className="w-5 h-5 mr-2 text-blue-600" />
                        Pro Tips
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div className="flex items-start space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                            <span>Speak clearly for better transcription accuracy</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                            <span>Choose the right category for better AI insights</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                            <span>Your recordings are automatically organized and searchable</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecordTab;
